import React from 'react';
import { Button } from '@progress/kendo-react-buttons';
import { Loader } from '@progress/kendo-react-indicators';
import { useInternationalization } from '@progress/kendo-react-intl';
import {
  useCurrentUserContext,
  useDocumentsContext,
  useGetAllFields,
  useGetContentType,
  useGetDocumentMetadata,
  useGlobalizationContext,
  useTranslation
} from '@cs-cms/state-management';
import {
  CONTENT_TYPE_FIELD,
  ContentTypeFieldResponse,
  CPS_ACTIONS_ALLOWED_FOR_DOCUMENT,
  CpsDocumentMetadata,
  CpsDocumentMetadataValue,
  CpsField,
  CpsSelectedDocument,
  DOC_ID_FIELD,
  PERMISSIONS_FIELD
} from '@cs-cms/types';
import { useCpsDialog } from '../../common/useCpsDialog';
import { useFieldGroups } from '../../form/hooks/useFieldGroups';
import {
  buildConfirmDialogMessage,
  countDocumentsByPermission,
  documentHasPermission
} from '../toolbar/filterActions/filterActionsHelpers';
import { PreviewDocument, useDocumentPreviewContext } from './DocumentPreviewContext';
import { DocumentPreviewDockFrame } from './useDocumentPreviewDock';
import { formatPreviewFieldValue, PreviewValueFormatters } from './formatPreviewFieldValue';

const FILE_NAME_FIELD = 'Cps_FileName';

interface DocumentPreviewPanelProps {
  frame: DocumentPreviewDockFrame;
}

function readMetadataValue(metadata: CpsDocumentMetadata, fieldName: string): CpsDocumentMetadataValue | null {
  const value = metadata[fieldName];
  if (value === undefined || value === null) {
    return null;
  }
  return value;
}

function resolvePreviewFieldLabel(fieldName: string, allFields: CpsField[] | undefined): string {
  const displayName = allFields?.find(field => field.name === fieldName)?.displayName;
  if (displayName) {
    return displayName;
  }
  return fieldName;
}

function toSelectedDocument(document: PreviewDocument): CpsSelectedDocument {
  return {
    [DOC_ID_FIELD]: document.documentId,
    [CONTENT_TYPE_FIELD]: document.contentType,
    [PERMISSIONS_FIELD]: document.permissions
  };
}

function resolveFileName(metadata: CpsDocumentMetadata | undefined): string | undefined {
  const fileName = metadata?.[FILE_NAME_FIELD];
  if (typeof fileName !== 'string' || fileName === '') {
    return undefined;
  }
  return fileName;
}

interface DocumentPreviewFieldListProps {
  fields: ContentTypeFieldResponse[];
  metadata: CpsDocumentMetadata;
  allFields: CpsField[] | undefined;
  formatters: PreviewValueFormatters;
}

const DocumentPreviewFieldList: React.FC<DocumentPreviewFieldListProps> = ({
  fields,
  metadata,
  allFields,
  formatters
}) => {
  return (
    <>
      {fields.map(field => (
        <div key={field.name} className="documentPreviewField">
          <span className="documentPreviewLabel">{resolvePreviewFieldLabel(field.name, allFields)}</span>
          <span className="documentPreviewValue">
            {formatPreviewFieldValue(readMetadataValue(metadata, field.name), field.type, formatters)}
          </span>
        </div>
      ))}
    </>
  );
};

export const DocumentPreviewPanel: React.FC<DocumentPreviewPanelProps> = ({ frame }) => {
  const { previewDocument, closeDocumentPreview } = useDocumentPreviewContext();
  const { downloadDocument, openDocument } = useDocumentsContext();
  const { showDialog, DialogComponent } = useCpsDialog();
  const { selectedIndex } = useCurrentUserContext();
  const { selectedLocale } = useGlobalizationContext();
  const { translate } = useTranslation(selectedLocale);
  const intl = useInternationalization();
  const indexId = selectedIndex?.id;

  const metadataRequest =
    previewDocument && indexId !== undefined
      ? {
          indexId,
          ctName: previewDocument.contentType,
          documentId: previewDocument.documentId
        }
      : undefined;

  const metadataQuery = useGetDocumentMetadata(metadataRequest);
  const contentTypeQuery = useGetContentType(previewDocument?.contentType, selectedLocale);
  const { data: allFields } = useGetAllFields(selectedLocale, indexId);

  const canDownloadFile = previewDocument
    ? documentHasPermission(toSelectedDocument(previewDocument), CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.DOWNLOAD)
    : false;

  const handleDownloadFile = (): void => {
    if (!previewDocument || !downloadDocument || !canDownloadFile) {
      return;
    }

    const counts = countDocumentsByPermission(
      [toSelectedDocument(previewDocument)],
      CPS_ACTIONS_ALLOWED_FOR_DOCUMENT.DOWNLOAD
    );
    const message = buildConfirmDialogMessage(
      translate,
      'custom.toolbar.actions.download.downloadDialogMessage',
      'custom.toolbar.actions.download.skippedDownloadDialogMessage',
      'custom.toolbar.actions.download.downloadProceed',
      counts
    );
    const contentType = previewDocument.contentType;
    const documentId = previewDocument.documentId;

    showDialog({
      title: translate('custom.toolbar.actions.download.downloadDialogTitle'),
      message,
      confirmButtonLabel: translate('custom.common.confirm'),
      discardButtonLabel: translate('custom.common.discard'),
      onConfirm: confirmed => {
        if (!confirmed) {
          return undefined;
        }
        downloadDocument(contentType, documentId);
        return undefined;
      }
    });
  };

  const handleOpenLink = (): void => {
    if (!previewDocument || !openDocument) {
      return;
    }

    openDocument(previewDocument.contentType, previewDocument.documentId);
  };

  const formatters = React.useMemo(
    (): PreviewValueFormatters => ({
      formatDate: value => intl.formatDate(value),
      formatDateTime: value => intl.formatDate(value, { datetime: 'short' }),
      formatBoolean: value => translate(value ? 'custom.common.yes' : 'custom.common.no')
    }),
    [intl, translate]
  );

  const visibleFields = React.useMemo(
    () => (contentTypeQuery.data?.fields ?? []).filter(field => !field.technical),
    [contentTypeQuery.data?.fields]
  );
  const { grouped, ungrouped } = useFieldGroups({
    fields: visibleFields,
    groups: contentTypeQuery.data?.groups
  });

  if (!previewDocument) {
    return null;
  }

  const metadata = metadataQuery.data?.data.metadata;
  const fileName = resolveFileName(metadata);
  const isLoading = (indexId !== undefined && metadataQuery.isPending) || contentTypeQuery.isPending;
  const hasLoadError = indexId === undefined || metadataQuery.isError || contentTypeQuery.isError;
  const panelTitle = translate('custom.toolbar.actions.form.displayMetadata');

  const renderBody = (): React.ReactNode => {
    if (isLoading) {
      return (
        <div className="documentPreviewStatus">
          <Loader size="medium" type="infinite-spinner" />
        </div>
      );
    }

    if (hasLoadError || !metadata) {
      return <p>{translate('custom.toolbar.actions.form.previewLoadFailed')}</p>;
    }

    return (
      <>
        {Array.from(grouped.entries()).map(([groupName, fields]) => (
          <section key={groupName} className="documentPreviewGroup">
            <h3 className="documentPreviewGroupTitle">{groupName}</h3>
            <DocumentPreviewFieldList
              fields={fields}
              metadata={metadata}
              allFields={allFields}
              formatters={formatters}
            />
          </section>
        ))}
        <DocumentPreviewFieldList
          fields={ungrouped}
          metadata={metadata}
          allFields={allFields}
          formatters={formatters}
        />
      </>
    );
  };

  return (
    <>
      <aside className="documentPreviewPanel" aria-label={panelTitle} style={{ top: frame.top, bottom: frame.bottom }}>
        <div className="documentPreviewHeader">
          <div className="documentPreviewHeading">
            <h2>{panelTitle}</h2>
            {fileName ? <p title={fileName}>{fileName}</p> : null}
          </div>
          <Button
            type="button"
            fillMode="flat"
            size="small"
            className="documentPreviewClose"
            aria-label={translate('custom.common.close')}
            title={translate('custom.common.close')}
            onClick={closeDocumentPreview}
          >
            ×
          </Button>
        </div>
        <div className="documentPreviewActions">
          <Button
            type="button"
            size="small"
            themeColor="primary"
            disabled={!canDownloadFile}
            onClick={handleDownloadFile}
          >
            {translate('custom.toolbar.actions.download.downloadFile')}
          </Button>
          <Button type="button" size="small" themeColor="primary" onClick={handleOpenLink}>
            {translate('custom.toolbar.actions.form.openLink')}
          </Button>
        </div>
        <div className="documentPreviewBody">{renderBody()}</div>
      </aside>
      {DialogComponent}
    </>
  );
};
